import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, Calendar, MapPin, Camera, Video, FileText, DollarSign, Check, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';

const GetQuotes = () => {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embed') === 'true';

  // Step 1: Phone verification, Step 2: Form, Step 3: Success
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateRequired: '',
    location: '',
    locationLat: null,
    locationLon: null,
    serviceRequired: [],
    brief: '',
    budget: ''
  });

  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadNumber, setLeadNumber] = useState('');
  const [cancellationUrl, setCancellationUrl] = useState('');
  const [expiresIn, setExpiresIn] = useState(300);
  const [expandingBrief, setExpandingBrief] = useState(false);

  const locationInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Initialize Google Maps autocomplete when form step (step 2) loads
  useEffect(() => {
    if (step === 2) {
      loadGoogleMapsScript(() => {
        const attemptInit = (attempts = 0) => {
          if (attempts > 20) return;

          const input = locationInputRef.current;
          if (input && window.google?.maps?.places) {
            initAutocomplete();
          } else {
            setTimeout(() => attemptInit(attempts + 1), 100);
          }
        };
        setTimeout(() => attemptInit(), 200);
      });
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [step]);

  const initAutocomplete = () => {
    const input = locationInputRef.current;
    if (!input || !window.google?.maps?.places) return;

    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'au' },
      fields: ['formatted_address', 'geometry']
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        setFormData(prev => ({
          ...prev,
          location: place.formatted_address,
          locationLat: place.geometry.location.lat(),
          locationLon: place.geometry.location.lng()
        }));
      }
    });
  };

  // OTP countdown timer
  useEffect(() => {
    if (otpSent && expiresIn > 0) {
      const timer = setTimeout(() => setExpiresIn(expiresIn - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpSent, expiresIn]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const services = prev.serviceRequired.includes(service)
        ? prev.serviceRequired.filter(s => s !== service)
        : [...prev.serviceRequired, service];
      return { ...prev, serviceRequired: services };
    });
  };

  const handleExpandWithAI = async () => {
    if (!formData.brief || formData.brief.trim().length < 10) {
      toast.error('Please write at least a few words about your project first');
      return;
    }

    setExpandingBrief(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/leads/expand-brief`,
        {
          brief: formData.brief,
          services: formData.serviceRequired,
          location: formData.location
        }
      );

      if (response.data.success) {
        setFormData(prev => ({ ...prev, brief: response.data.expanded_brief }));
        toast.success('Brief expanded with AI suggestions!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to expand brief');
    } finally {
      setExpandingBrief(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.phoneNumber || formData.phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/otp/send`,
        { phone_number: formData.phoneNumber }
      );

      if (response.data.success) {
        toast.success('Verification code sent to your mobile');
        setOtpSent(true);
        setExpiresIn(response.data.expires_in || 300);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 4) {
      toast.error('Please enter the 4-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/otp/verify`,
        {
          phone_number: formData.phoneNumber,
          otp_code: otpCode
        }
      );

      if (response.data.verified) {
        toast.success('Phone verified! Please complete the form.');
        setStep(2); // Move to full form
      } else {
        toast.error(response.data.message || 'Invalid code');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLead = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email ||
        !formData.dateRequired || !formData.location ||
        formData.serviceRequired.length === 0 || !formData.brief) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/leads`,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone_number: formData.phoneNumber,
          date_required: formData.dateRequired,
          location: formData.location,
          location_lat: formData.locationLat,
          location_lon: formData.locationLon,
          service_required: formData.serviceRequired,
          brief: formData.brief,
          budget: formData.budget
        }
      );

      if (response.data.success) {
        setLeadNumber(response.data.lead_number);
        setCancellationUrl(response.data.cancellation_url);
        setStep(3);
        toast.success('Your request has been submitted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] ${isEmbedded ? 'p-4' : ''}`}>
      {/* Header - hidden when embedded */}
      {!isEmbedded && (
        <div className="bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] shadow-2xl">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <img
              src="/assets/shootzu-logo-white.png"
              alt="Shootzu"
              className="h-16"
            />
          </div>
        </div>
      )}

      <div className={`max-w-6xl mx-auto ${isEmbedded ? 'px-2 py-4' : 'px-6 py-12'}`}>

        {/* Step 1: Phone Verification */}
        {step === 1 && (
          <div className="max-w-xl mx-auto">
            {/* Hero */}
            {!isEmbedded && (
              <div className="text-center mb-12">
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
                  Get Free Quotes
                </h1>
                <p className="text-xl text-gray-300">
                  Connect with verified photographers & videographers in your area
                </p>
              </div>
            )}

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-[#7B3FF2] to-[#9D5CFF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify Your Mobile</h2>
                <p className="text-gray-300">We'll send you a verification code to get started</p>
              </div>

              {!otpSent ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white font-medium mb-2">
                      <Phone className="inline w-4 h-4 mr-1" />
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 text-lg rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50 transition-all"
                      placeholder="0412 345 678"
                      autoFocus
                    />
                  </div>

                  <button
                    onClick={handleSendOTP}
                    disabled={loading || !formData.phoneNumber}
                    className="w-full py-4 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white text-lg font-bold rounded-xl hover:shadow-2xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        Send Verification Code
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-center text-gray-300 mb-4">
                    Code sent to <strong className="text-white">{formData.phoneNumber}</strong>
                  </p>

                  <input
                    type="text"
                    maxLength={4}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-4 text-center text-3xl font-bold tracking-[0.5em] rounded-xl bg-white/20 border border-white/30 text-white focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                    placeholder="0000"
                    autoFocus
                  />

                  <p className="text-center text-gray-300">
                    Expires in: <strong className="text-white">{formatTime(expiresIn)}</strong>
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setOtpSent(false);
                        setOtpCode('');
                      }}
                      className="flex-1 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all"
                    >
                      Change Number
                    </button>
                    <button
                      onClick={handleVerifyOTP}
                      disabled={loading || otpCode.length !== 4}
                      className="flex-1 py-3 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white font-bold rounded-xl hover:shadow-lg disabled:opacity-50 transition-all"
                    >
                      {loading ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>

                  {expiresIn === 0 && (
                    <button
                      onClick={handleSendOTP}
                      className="w-full py-2 text-[#9D5CFF] hover:text-white transition-colors"
                    >
                      Resend Code
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* How it works */}
            {!isEmbedded && (
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#9D5CFF] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">Verify</h3>
                  <p className="text-gray-300 text-sm">Quick mobile verification</p>
                </div>

                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#9D5CFF] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">Describe</h3>
                  <p className="text-gray-300 text-sm">Tell us about your event</p>
                </div>

                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#9D5CFF] rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">Get Quotes</h3>
                  <p className="text-gray-300 text-sm">Receive quotes from pros</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Full Form */}
        {step === 2 && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
              {/* Verified badge */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <span className="text-green-400 font-medium">Verified: {formData.phoneNumber}</span>
              </div>

              <h2 className="text-3xl font-bold text-white mb-2 text-center">Tell Us About Your Project</h2>
              <p className="text-gray-300 text-center mb-8">Fill in the details and we'll connect you with professionals</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                    placeholder="Smith"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    <Mail className="inline w-4 h-4 mr-1" />
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    Event Date <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    name="dateRequired"
                    value={formData.dateRequired}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white font-medium mb-2">
                    <MapPin className="inline w-4 h-4 mr-1" />
                    Event Location <span className="text-red-400">*</span>
                  </label>
                  <input
                    ref={locationInputRef}
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                    placeholder="Start typing your address..."
                  />
                  <p className="text-xs text-gray-400 mt-1">Australian addresses only</p>
                </div>
              </div>

              {/* Services */}
              <div className="mt-6">
                <label className="block text-white font-medium mb-3">
                  Services Required <span className="text-red-400">*</span>
                </label>
                <div className="flex flex-wrap gap-4">
                  <button
                    type="button"
                    onClick={() => handleServiceToggle('photography')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      formData.serviceRequired.includes('photography')
                        ? 'bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white shadow-lg'
                        : 'bg-white/20 text-white border border-white/30 hover:border-white/50'
                    }`}
                  >
                    <Camera className="w-5 h-5" />
                    Photography
                  </button>

                  <button
                    type="button"
                    onClick={() => handleServiceToggle('videography')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      formData.serviceRequired.includes('videography')
                        ? 'bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white shadow-lg'
                        : 'bg-white/20 text-white border border-white/30 hover:border-white/50'
                    }`}
                  >
                    <Video className="w-5 h-5" />
                    Videography
                  </button>
                </div>
              </div>

              {/* Brief */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white font-medium">
                    <FileText className="inline w-4 h-4 mr-1" />
                    Project Details <span className="text-red-400">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleExpandWithAI}
                    disabled={expandingBrief || !formData.brief || formData.brief.length < 10}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 shadow-lg"
                  >
                    {expandingBrief ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Expanding...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5" />
                        Expand with AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  name="brief"
                  value={formData.brief}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50 resize-none"
                  placeholder="Tell us about your event... Be as detailed as possible!"
                />
                <p className="text-sm text-gray-400 mt-2">
                  💡 Write a few words, then click "Expand with AI" for a professional brief!
                </p>
              </div>

              {/* Budget */}
              <div className="mt-6">
                <label className="block text-white font-medium mb-2">
                  <DollarSign className="inline w-4 h-4 mr-1" />
                  Budget (Optional)
                </label>
                <input
                  type="text"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:border-[#7B3FF2] focus:ring-2 focus:ring-[#7B3FF2]/50"
                  placeholder="e.g., $500-$1000"
                />
              </div>

              {/* Submit */}
              <div className="mt-8">
                <button
                  onClick={handleSubmitLead}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white text-lg font-bold rounded-xl hover:shadow-2xl transition-all disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 shadow-2xl text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-14 h-14 text-white" />
              </div>

              <h2 className="text-4xl font-bold text-white mb-4">Request Submitted!</h2>
              <p className="text-xl text-gray-300 mb-6">
                Your request has been received and is being reviewed.
              </p>

              <div className="bg-white/20 rounded-2xl p-6 mb-6">
                <p className="text-gray-300 mb-2">Your Request Number:</p>
                <p className="text-3xl font-bold text-white">{leadNumber}</p>
              </div>

              <div className="space-y-4 text-left bg-white/10 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-bold text-white mb-3">What Happens Next?</h3>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#7B3FF2] rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <p className="text-gray-300">We'll review your request within 24 hours</p>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#7B3FF2] rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <p className="text-gray-300">Verified professionals in your area will be notified</p>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#7B3FF2] rounded-full flex items-center justify-center text-white font-bold">3</div>
                  <p className="text-gray-300">They'll contact you directly with quotes</p>
                </div>
              </div>

              <button
                onClick={() => window.location.href = 'https://shootzu.com'}
                className="px-8 py-3 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white font-bold rounded-xl hover:shadow-lg transition-all"
              >
                Visit Shootzu Platform
              </button>

              {cancellationUrl && (
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm text-gray-400 mb-2">Need to cancel?</p>
                  <a href={cancellationUrl} className="text-[#7B3FF2] hover:text-[#9D5CFF] underline text-sm">
                    Cancel request
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!isEmbedded && (
        <div className="bg-black/30 py-8 mt-12">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 Shootzu. Australian professionals only. All rights reserved.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetQuotes;
